import { bulletTypes } from "~defs/BulletTypeDefs";
import { Bullet } from "./Bullet";
import { Engine } from "./Engine";
import { asyncLogLevel, L, log, logLevel } from "./log";
import {
  BackgroundUpdate,
  BulletFiredUpdate,
  ClockUpdate,
  GameInfoUpdate,
  PlayerUpdate,
  PlayerDisconnectedUpdate,
  PlayerJoinedUpdate,
  Update,
  UpdateType,
  PlayerHitUpdate,
  PlayerRespawnUpdate,
  ChatUpdate,
} from "../../../backend/src/lib/types";
import { Background } from "./Background";
import { Player } from "./Player";
import { Sequencer } from "./net";

type Handler = (update: any) => void;
export class RemoteInterface {
  private clockDelta: number;
  ws: WebSocket;
  engine: Engine;
  ready: boolean;
  room: string;
  private listeners: Map<UpdateType, Handler[]> = new Map();

  static TICK_MS = 25;

  constructor(
    engine: Engine,
    room: string,
    playerName: string,
    playerColour: string
  ) {
    console.log(`Joining room ${room}`);
    this.ws = new WebSocket(`ws/${room}`);
    this.ws.onopen = () => this.init(playerName, playerColour);
    this.ws.onclose = () => {
      this.engine.pusher(`Disconnected from room ${room}`);
      this.ready = false;
      this.engine.disconnectHandler();
    };
    this.room = room;
    this.engine = engine;
    this.ready = false;
    this.clockDelta = 0;
  }

  init = asyncLogLevel(
    L.INFO,
    async (playerName: string, playerColour: string) => {
      this.engine.pusher(`Connecting to room ${this.room}`);

      const playersReady = new Set<number>();
      let gotBackground = false;

      const sequencer = new Sequencer(this.ws, [
        {
          handler: (data, resolve) => {
            try {
              log(L.VERBOSE, `Got message from time sync processor: ${data}`);
              const update = JSON.parse(data.toString()) as ClockUpdate;
              if (update.type !== UpdateType.CLOCK) return;
              this.clockDelta = Date.now() - update.time;
              this.engine.tick = this.tick();
              this.sendUpdate({
                type: UpdateType.CLOCK,
                time: Date.now(),
              });
              log(L.DEBUG, "Completed time sync");
              resolve();
            } catch {}
          },
        },
        {
          handler: (data, resolve) => {
            try {
              const update = JSON.parse(data.toString()) as GameInfoUpdate;
              if (update.type !== UpdateType.GAME_INFO) return;
              this.engine.players = update.players.map(
                (x) =>
                  new Player(
                    this.engine,
                    0,
                    x.id,
                    x.name,
                    x.colour,
                    x.kills,
                    x.deaths
                  )
              );
              log(L.DEBUG, "Completed game info sync");
              this.ws.addEventListener(
                "message",
                this.incomingMessage.bind(this)
              );
              sequencer.replay(this.incomingMessage.bind(this));
              resolve();
            } catch {}
          },
        },
        {
          init: () => {
            const newX = Math.random() * Background.WIDTH;
            const newId = this.engine.players.length;
            this.engine.players.push(
              new Player(
                this.engine,
                newX,
                newId,
                playerName,
                playerColour,
                0,
                0
              )
            );
            this.engine.setScoreboard(
              this.engine.players.map((x) => ({
                name: x.name,
                colour: x.colour,
                kills: x.kills,
                deaths: x.deaths,
              }))
            );
            this.engine.localPlayerId = newId;
            this.sendUpdate({
              type: UpdateType.PLAYER_JOINED,
              x: newX,
              id: newId,
              name: playerName,
              colour: playerColour,
            });
          },
          handler: (data, resolve) => {
            try {
              const update = JSON.parse(data.toString()) as
                | PlayerUpdate
                | PlayerDisconnectedUpdate
                | BackgroundUpdate;
              switch (update.type) {
                case UpdateType.PLAYER_UPDATE:
                  log(L.VERBOSE, `Got update from ${update.pid}`);
                  playersReady.add(update.pid);
                  break;
                case UpdateType.PLAYER_DISCONNECTED:
                  log(L.VERBOSE, `Got disconnected from ${update.pid}`);
                  playersReady.add(update.pid);
                  break;
                case UpdateType.BACKGROUND_UPDATE:
                  if (update.data.length === Background.WIDTH)
                    gotBackground = true;
                  break;
              }
              if (
                playersReady.size === this.engine.players.length - 1 &&
                gotBackground
              )
                resolve();
            } catch {}
          },
        },
      ]);
      await sequencer.promise();
      this.ready = true;
      this.engine.pusher("Connected!");
    }
  );

  public time() {
    return Date.now() - this.clockDelta;
  }

  public tick() {
    return Math.floor(this.time() / RemoteInterface.TICK_MS);
  }

  sendUpdate = logLevel(L.INFO, (update: Update) => {
    this.ws.send(JSON.stringify(update));
  });

  awaitMessage<T>(
    init: () => void,
    handler: (
      e: MessageEvent,
      resolve: (value: T) => void
    ) => void | Promise<void>
  ): Promise<T> {
    return new Promise<T>((resolve) => {
      const wrappedHandler = async (e: MessageEvent) => {
        const resolver = (value: T) => {
          this.ws.removeEventListener("message", wrappedHandler);
          resolve(value);
        };
        await handler(e, resolver);
      };
      this.ws.addEventListener("message", wrappedHandler);
      init();
    });
  }

  private incomingMessage = logLevel(L.INFO, async (e: MessageEvent) => {
    const { data: dataRaw } = e;
    log(L.VERBOSE, `Incoming message: ${dataRaw}`);
    try {
      const data = JSON.parse(dataRaw) as Update;
      this.listeners.get(data.type)?.forEach((f) => f(data));
    } catch {}
  });

  handler(type: UpdateType, func: Handler) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type)!.push(func);
  }

  clock = this.handler(UpdateType.CLOCK, (data: ClockUpdate) => {
    this.clockDelta = this.clockDelta * 0.75 + (Date.now() - data.time) * 0.25;
    this.sendUpdate({
      type: UpdateType.CLOCK,
      time: Date.now(),
    });
  });

  playerUpdate = this.handler(
    UpdateType.PLAYER_UPDATE,
    (data: PlayerUpdate) => {
      if (data.tick > this.engine.tick) {
        this.engine.schedule(data.tick, (engine) => {
          engine.players[data.pid].direction = data.dir;
          engine.players[data.pid].x = data.x;
          engine.players[data.pid].angle = data.angle;
          engine.players[data.pid].angleDirection = data.angleDir;
          engine.players[data.pid].vx = data.vx;
        });
      } else {
        const targetPlayer = this.engine.players[data.pid];
        targetPlayer.direction = data.dir;
        targetPlayer.x = data.x;
        targetPlayer.vx = data.vx;
        targetPlayer.angle = data.angle;
        targetPlayer.angleDirection = data.angleDir;
        Array.from({ length: this.engine.tick - data.tick }).forEach(() =>
          this.engine.players[data.pid].update(this.engine.getUpdateContext())
        );
      }
    }
  );

  bulletFired = this.handler(
    UpdateType.BULLET_FIRED,
    (data: BulletFiredUpdate) => {
      const spawnArgs = {
        sourceRemote: true,
        x: data.x,
        y: data.y,
        vx: data.vx,
        vy: data.vy,
        type: bulletTypes[data.bulletType],
        owner: this.engine.players[data.owner],
      };
      if (data.tick >= this.engine.tick) {
        this.engine.schedule(data.tick, (engine) => {
          engine.bullets.spawnBullet(spawnArgs);
        });
      } else {
        const newBullet = this.engine.bullets.spawnBullet(spawnArgs);
        Array.from({ length: this.engine.tick - data.tick }).forEach(() =>
          newBullet.update(this.engine.getUpdateContext())
        );
      }
    }
  );

  backgroundUpdate = this.handler(
    UpdateType.BACKGROUND_UPDATE,
    (data: BackgroundUpdate) => {
      data.data.forEach((y, i) => {
        if (data.x + i < 0 || data.x + i >= Background.WIDTH) return;
        this.engine.background.heightMap[data.x + i] = y;
      });
      this.engine.background.refresh(data.x, data.x + data.data.length);
    }
  );

  newPlayer = this.handler(
    UpdateType.PLAYER_JOINED,
    (data: PlayerJoinedUpdate) => {
      const localPlayer = this.engine.getLocalPlayer();
      this.sendUpdate({
        type: UpdateType.PLAYER_UPDATE,
        angle: localPlayer.angle,
        angleDir: localPlayer.angleDirection,
        x: localPlayer.x,
        vx: localPlayer.vx,
        dir: localPlayer.direction,
        pid: localPlayer.id,
        tick: this.engine.tick,
      });
      this.engine.players.push(
        new Player(this.engine, data.x, data.id, data.name, data.colour, 0, 0)
      );
      this.engine.setScoreboard(
        this.engine.players.map((x) => ({
          name: x.name,
          colour: x.colour,
          kills: x.kills,
          deaths: x.deaths,
        }))
      );
    }
  );

  playerDisconnected = this.handler(
    UpdateType.PLAYER_DISCONNECTED,
    (data: PlayerDisconnectedUpdate) => {
      if (!this.engine.players[data.pid]) return;
      this.engine.players[data.pid].connected = false;
    }
  );

  playerHit = this.handler(UpdateType.PLAYER_HIT, (data: PlayerHitUpdate) => {
    if (!this.engine.players[data.pid]) return;
    this.engine.players[data.pid].hp = data.hp;
  });

  playerRespawn = this.handler(
    UpdateType.PLAYER_RESPAWN,
    (data: PlayerRespawnUpdate) => {
      const player = this.engine.players[data.pid];
      if (!player) return;
      this.engine.pusher(
        `${this.engine.players[data.causePid].name} ${
          bulletTypes[data.causeType].name
        }'d ${data.pid === data.causePid ? "themself" : player.name}`
      );
      player.hp = Player.MAX_HP;
      player.x = data.x;
      player.y = this.engine.background.heightMap[data.x];
      player.vx = 0;
      player.respawnState = 25;

      player.deaths++;
      this.engine.players[data.causePid].kills++;
      this.engine.setScoreboard(
        this.engine.players.map((x) => ({
          name: x.name,
          colour: x.colour,
          kills: x.kills,
          deaths: x.deaths,
        }))
      );
    }
  );

  chat = this.handler(UpdateType.CHAT, (data: ChatUpdate) => {
    const player = this.engine.players[data.pid];
    if (!player) return;
    this.engine.pusher(`${player.name}: ${data.text}`, player.colour);
  });
}
